import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { navActions } from "@/app/store";
import { NAV_LINKS, PROFILE } from "@/data/portfolio";
import { COMMAND } from "@/constants/testIds";
import {
  FiHome,
  FiUser,
  FiCode,
  FiBriefcase,
  FiFolder,
  FiAward,
  FiMail,
  FiGithub,
  FiLinkedin,
  FiDownload,
} from "react-icons/fi";

const iconMap = {
  home: <FiHome />,
  about: <FiUser />,
  skills: <FiCode />,
  experience: <FiBriefcase />,
  projects: <FiFolder />,
  education: <FiAward />,
  contact: <FiMail />,
};

export default function CommandPalette() {
  const open = useSelector((s) => s.nav.commandOpen);
  const dispatch = useDispatch();
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);

  const allItems = useMemo(
    () => [
      ...NAV_LINKS.map((l) => ({
        id: `nav-${l.id}`,
        label: `Go to ${l.label}`,
        kind: "Section",
        icon: iconMap[l.id] || <FiCode />,
        action: () => {
          dispatch(navActions.closeCommand());
          const el = document.getElementById(l.id);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        },
      })),
      {
        id: "act-resume",
        label: "Download Resume",
        kind: "Action",
        icon: <FiDownload />,
        action: () => window.open(PROFILE.resumeUrl, "_blank"),
      },
      {
        id: "act-github",
        label: "Open GitHub",
        kind: "Link",
        icon: <FiGithub />,
        action: () => window.open(PROFILE.github, "_blank"),
      },
      {
        id: "act-linkedin",
        label: "Open LinkedIn",
        kind: "Link",
        icon: <FiLinkedin />,
        action: () => window.open(PROFILE.linkedin, "_blank"),
      },
      {
        id: "act-email",
        label: `Email ${PROFILE.email}`,
        kind: "Link",
        icon: <FiMail />,
        action: () => (window.location.href = `mailto:${PROFILE.email}`),
      },
    ],
    [dispatch]
  );

  const filtered = useMemo(() => {
    if (!query.trim()) return allItems;
    const q = query.toLowerCase();
    return allItems.filter((i) => i.label.toLowerCase().includes(q));
  }, [query, allItems]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        dispatch(navActions.toggleCommand());
      }
      if (e.key === "Escape" && open) dispatch(navActions.closeCommand());
      if (open) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setActiveIdx((i) => Math.min(i + 1, filtered.length - 1));
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          setActiveIdx((i) => Math.max(i - 1, 0));
        }
        if (e.key === "Enter" && filtered[activeIdx]) {
          e.preventDefault();
          filtered[activeIdx].action();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dispatch, open, filtered, activeIdx]);

  useEffect(() => {
    setActiveIdx(0);
  }, [query, open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => dispatch(navActions.closeCommand())}
          className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-xl flex items-start justify-center pt-[15vh] px-4"
        >
          <motion.div
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, y: -20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.98 }}
            transition={{ type: "spring", damping: 25, stiffness: 280 }}
            data-testid={COMMAND.palette}
            className="w-full max-w-xl rounded-2xl overflow-hidden border border-[color:var(--border-strong)] bg-[color:var(--surface)] shadow-2xl"
          >
            <div className="flex items-center gap-3 px-4 py-3 border-b border-[color:var(--border)]">
              <span className="label-mono text-[color:var(--accent)]">⌘K</span>
              <input
                data-testid={COMMAND.input}
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Type to search sections, actions or links..."
                className="flex-1 bg-transparent outline-none text-sm font-body"
              />
            </div>
            <div className="max-h-[50vh] overflow-y-auto py-2">
              {filtered.length === 0 && (
                <div className="px-4 py-6 text-sm font-mono text-[color:var(--text-muted)]">
                  No results.
                </div>
              )}
              {filtered.map((item, i) => (
                <button
                  key={item.id}
                  data-testid={COMMAND.item(item.id)}
                  onMouseEnter={() => setActiveIdx(i)}
                  onClick={item.action}
                  className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${
                    i === activeIdx
                      ? "bg-[color:var(--surface-2)] text-[color:var(--text)]"
                      : "text-[color:var(--text-muted)]"
                  }`}
                >
                  <span className="w-8 h-8 rounded-md border border-[color:var(--border)] flex items-center justify-center">
                    {item.icon}
                  </span>
                  <span className="flex-1 font-body text-sm">{item.label}</span>
                  <span className="label-mono">{item.kind}</span>
                </button>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
